import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent, DialogService, ErrorsUtil, MessageBoxComponent, MessageContent, TrustHtmlPipe, WaitComponent } from '@sircon/sircon-ui-framework';
import { TermsOfService, TermsOfServiceHttpService } from 'src/providers/terms-of-service-http.service';
import { UserContextService } from 'src/services/user-context.service';
import * as less from 'less';
import { catchError, finalize, map, of, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-terms-of-service',
    templateUrl: './terms-of-service.component.html',
    styleUrls: ['./terms-of-service.component.less'],
    imports: [WaitComponent, MessageBoxComponent, TrustHtmlPipe]
})
export class TermsOfServiceComponent extends BaseComponent implements OnInit {

    @Input() dialogId: string;
    @Input() termsCacheKey: string;

    defaultDialogId = 'terms-of-service';
    loading = true;
    errors: MessageContent = ErrorsUtil.create();
    terms: string;
    closeDialog: (dialogId: string) => void;

    constructor(private route: ActivatedRoute,
                private dialogService: DialogService,
                private userContextService: UserContextService,
                private termsOfServiceHttpService: TermsOfServiceHttpService) {
        super();
        this.closeDialog = this.dialogService.close.bind(this.dialogService);
    }

    ngOnInit(): void {
        this.determineTermsOfServiceToLoad();
        this.getTermsOfService();
    }

    determineTermsOfServiceToLoad(): void {
        if (this.termsCacheKey) {
            this.dialogId = this.dialogId || this.defaultDialogId;
        } else if (this.dialogService.getContext() && this.dialogService.getContext().termsCacheKey) {
            // FCRA is a special type of terms of service and needs to be available in addition to normal terms that are in the footer
            // two dialogs need to be available at the same time... so context can be passed in to override the normal footer
            // behavior.
            const context = this.dialogService.getContext();
            this.termsCacheKey = context.termsCacheKey;
            this.dialogId = context.dialogId;
            this.dialogService.clearContext();
        } else {
            this.dialogId = this.defaultDialogId;
            // Two pages do not have a valid user for user context, because they are unauthenticated pages
            // resetPassword should only get here if the user is a pedge migrated user, hence they must be an individual
            if (this.route.snapshot.routeConfig?.path === 'signup/acceptInvitation' ||
                this.route.snapshot.routeConfig?.path === 'inviteSignup/signup' ||
                this.route.snapshot.routeConfig?.path === 'resetPassword') {
                this.termsCacheKey = 'individual';
            } else if (this.route.snapshot.routeConfig?.path === 'signup/agency/review') {
                this.termsCacheKey = 'firm';
            } else {
                this.termsCacheKey = this.userContextService.userContext.account.accountType;
            }
        }
    }

    getTermsOfService(): void {
        let termsOfService$;
        switch (this.termsCacheKey) {
            case 'individual':
                termsOfService$ = this.termsOfServiceHttpService.getIndividualTermsOfServiceAsObservable();
                break;
            case 'firm':
                termsOfService$ = this.termsOfServiceHttpService.getFirmTermsOfServiceAsObservable();
                break;
            case 'agency':
                termsOfService$ = this.termsOfServiceHttpService.getAgencyTermsOfServiceAsObservable();
                break;
            case 'educationProvider':
                termsOfService$ = this.termsOfServiceHttpService.getEducationProviderTermsOfServiceAsObservable();
                break;
            case 'fcra':
                termsOfService$ = this.termsOfServiceHttpService.getFCRATermsOfServiceAsObservable();
                break;
        }

        termsOfService$.pipe(
            map((response: TermsOfService) => {
                this.terms = this.handleStyleTags(response.content);
            }),
            catchError((error: HttpErrorResponse) => {
                this.errors = ErrorsUtil.mapResponse(error);
                return of(error);
            }),
            finalize(() => {
                this.loading = false;
            }),
            takeUntil(this.ngUnsubscribe)
        ).subscribe();
    }

    /**
     * Terms of Service content can contain markup, including <style> tags. In order to prevent any styles configured here from polluting
     * the rest of the application, parse out the content inside the <style> tags and make the specificity only match this component.
     * Render the style as LESS
     *
     * @param content - string of terms of service HTML, retrieved from an API call
     */
    handleStyleTags(content: string): string {
        let newContent = content;

        /*
         * As of 3/20/2020, IE 11, Safari 13 and Firefox 74-76 cannot support regular expression lookbehinds
         * Maybe one day they will, check here: https://caniuse.com/#feat=js-regexp-lookbehind
         *
         * This change will include the <style> tag in the matching result, but the RegExp will return 2 elements
         * in the array: the full match and the matching group defined with (.*?).  Using only the second element in
         * the array will work just as same as the lookbehind.
         */
        const contentBetweenStyleTags = new RegExp('<style>(.*?)(?=</style>)');
        const styleContent = content.match(contentBetweenStyleTags);

        // Only process changes if content contains <style> tags
        if (styleContent) {
            const capturedGroupContent = styleContent.pop();

            less.render(`.terms-of-service { ${capturedGroupContent} }`, (err, result) => {
                if (err) {
                    return;
                }
                newContent = content.replace(capturedGroupContent, result.css);
            });
        }

        return newContent;
    }
}
